import { Component } from "./Component.js";
import { Router } from "./Router.js";
import { StatusOverlay } from "./components/StatusOverlay.js";
import { Toaster } from "./components/Toaster.js";
import { Logger } from "../tracing/logger.js";
import { Menu } from "./controls/menu.js";
import { Storage } from "../data/Storage.js";
import { i18n } from "../globalization/i18n.js";
import * as http from "../utils/http.js";
import { defineElement } from "domvm";
import { Services } from "../services/Services.js";
import { isFunction } from "../data/utils.js";
import { getAuthSuccessRedirect } from "./application-auth-helpers.js";

/**
 * @param {AuthManager} authManager - Autentizační manager.
 * @param {Array<RouteDef>} routes - Definice aplikačních rout.
 * @param {Array<RouteDef>} defaultRoutes - Jména speciálních rout. Musí odpovídat jménům v routes.
 */
export class Application extends Component {

    constructor({
        authManager,
        routes,
        defaultRoutes,
        mainMenuDefs = [],
    }) {

        if (!authManager) throw new Error('[Application] "authManager" is required.');
        if (!routes) throw new Error('[Application] "routes" is required.');
        if (!defaultRoutes) throw new Error('[Application] "defaultRoutes" is required.');

        super({});

        this.logger = new Logger();
        this.logger.onLog((e) => {

            switch (e.level) {
                case Logger.ERROR:
                    this.toaster.error(e.message, null, { details: e.data });
                    break;
                case Logger.WARNING:
                    this.toaster.warning(e.message, null, { details: e.data });
                    break;
                case Logger.SUCCESS:
                    this.toaster.success(e.message, null, { details: e.data });
                    break;
                default:
                    this.toaster.info(e.message, null, { details: e.data });
                    break;
            }

        });

        this.router = new Router({
            app: this,
            authManager,
            routes,
            defaultRoutes,
            onRouteChange: (routeMatch) => this._handleRouteChange(routeMatch),
            defaultRouteName: defaultRoutes.defaultRouteName,
            loginRouteName: defaultRoutes.loginRouteName,
            forbiddenRouteName: defaultRoutes.forbiddenRouteName,
            notFoundRouteName: defaultRoutes.notFoundRouteName,
        });

        this.toaster = new Toaster(this, {
            toastTimeOut: 5000,
            position: Toaster.POSITION_BOTTOM_RIGHT,
        });

        this.statusOverlay = new StatusOverlay(this);

        this.activeModule = null;
        this.activeModuleKey = null;

        this.authManager = authManager;
        this.mainMenuDefs = mainMenuDefs || [];
        authManager.onLogin((ev) => {
            if (ev?.remote) {
                this.mainMenu.refresh();
                this.invalidate();
                return;
            }
            this._handleAuthSuccess();
        });
        authManager.onLogout((ev) => { this._handleAuthFailure(); });
        authManager.onAuthError((error) => {
            if (!this.authManager.isAuthenticated()) {
                this._handleAuthFailure();
                return;
            }
            this.mainMenu.refresh();
            this.invalidate();
        });
        authManager.onTokenRefreshed((ev) => {
            if (!ev?.remote) {
                return;
            }
            this.mainMenu.refresh();
            this.invalidate();
        });

        // DO NOT use parent autoregistration - special rendering position
        this.mainMenu = new Menu(null, { items: () => this.getMainMenuDefs() });
        this.mainMenu.parent = this;

        this.dataStorage = new Storage();

        this.$initialized = true;

    };

    isInitialized() {

        return this.$initialized;

    };

    async prepare() {

        await this.mainMenu.prepare();
        await this.mainMenu.refresh();

    };

    async refresh() {

        await this.mainMenu.refresh();

    };

    // --------------------------------------------------------------------------------------------

    getParent() { return null; };

    getApp() { return this; };

    getAuthManager() { return this.authManager; };

    invalidate(level = 0) { this._view?.redraw(); }

    // --------------------------------------------------------------------------------------------

    getAppLogo() { return '/img/app-logo.png'; };

    getAppName() { return 'Application'; };

    getAvailableLocales() {

        return [
            {
                code: 'en',
                name: 'English'
            },
            {
                code: 'en-gb',
                name: 'English (Great Britain)'
            },
            {
                code: 'cs',
                name: 'České'
            },
        ];

    };

    getLocale() {

        const config = Services.get('config');
        return ((typeof this.locale == 'function') ? this.locale.call(this) : this.locale) || config.locale;

    };

    async setLocale(locale) {

        this.setBusyFlag();
        try {
            const changed = await i18n.setLocale(locale, true);
            if (changed)
                http.setCookie('locale', locale);
            this.invalidate();
        } catch (err) {
            this.handleError(err)
        } finally {
            this.clearBusyFlag();
        }

    };

    // -------------------------------------------------------------------------------------------- 

    isApplicationBusy() { return this.statusOverlay.isVisible(); };

    setBusyFlag() { this.statusOverlay.show(); };

    clearBusyFlag() { this.statusOverlay.hide(); };

    // --------------------------------------------------------------------------------------------

    getMainMenuDefs() {

        return this.mainMenuDefs || [];

    };

    // -------------------------------------------------------------------------------------------- Route hash & URL helpers

    getModuleUrl(modulePath) { return config.baseUrl + '#' + modulePath; };

    getApiUrl(apiPath, params) { return config.baseUrl + '/api/' + apiPath; };

    getRoute(routeName) { return this.router._findRoute(routeName); };

    getRouteDef(routeName) { return this.getRoute()?.def; }

    getRouteHash(routeName, params = {}, query = {}) { return this.router.getRouteHash(routeName, params, query); };

    getRouteUrl(routeName, params, query) { return this.router.getRouteUrl(routeName, params, query); };

    getActiveRoute() { return this.router.getActiveRoute(); };

    getActiveRouteDef() { return this.getActiveRoute()?.def; };

    getActiveRouteName() { return this.getActiveRoute()?.def?.name; };

    getActiveRouteParams() { return this.getActiveRoute()?.params; };

    getActiveRouteQuery() { return this.getActiveRoute()?.query; };

    // -------------------------------------------------------------------------------------------- 

    getAcitveModule() { return this.activeModule; };

    moduleChanged(module) { /* do nothing */ };

    // -------------------------------------------------------------------------------------------- Navigation & Routing

    /** 
     * Changes nav, creates new nav history record.
     */
    navigateTo(url) { this.router.navigateTo(url); };

    navigateBack() { this.router.navigateBack(); };

    routeTo(routeName, params = {}, query = {}) { return this.router.routeTo(routeName, params, query); };

    routeToDefaultRoute() {

        this.mainMenu.active = false;
        this.router.routeToDefault();

    };

    // --------------------------------------------------------------------------------------------

    _handleAuthSuccess() {

        this.mainMenu.refresh();

        const routeRedirect = getAuthSuccessRedirect(this.getActiveRouteQuery());

        if (routeRedirect) {
            this.router.navigateToHash(routeRedirect);
            return;
        }

        this.routeToDefaultRoute();

    };

    _handleAuthFailure() {

        this.mainMenu.refresh();
        this.routeToDefaultRoute();

    };

    // -------------------------------------------------------------------------------------------- Routing handlers

    async _handleRouteChange(routeMatch) {

        this.setBusyFlag();
        try {

            if (routeMatch.notFound) {
                this.activeModule = null;
                this.invalidate();
                return;
            }

            const routeDef = routeMatch.def;

            if (!routeDef) {
                this.activeModule = null;
                this.invalidate();
                return;
            }

            let moduleKey = routeDef.name;

            if (moduleKey != this.activeModuleKey) {

                try {

                    const moduleInstance = await this._createModuleFromRouteDef(routeDef, routeMatch);
                    moduleInstance.parent = this;

                    this.activeModule = moduleInstance;
                    this.activeModuleKey = moduleKey;

                    if (isFunction(this.activeModule.prepare)) {
                        const activeRoute = routeMatch;
                        const routeOptions = activeRoute.def?.options ?? {};
                        await this.activeModule.prepare(routeOptions, activeRoute);
                    }

                    this.mainMenu.hide();
                    this.mainMenu.activateNodeByRoute(routeDef.name);

                } catch (err) {

                    console.error('Failed to load module for route:', routeDef.name, err);
                    // TODO: error modul / stránka

                    this.activeModule = null;
                    this.activeModuleKey = null;

                    this.invalidate();

                    return;

                }

            }

            if (this.activeModule && typeof this.activeModule.refresh === 'function') {
                const activeRoute = routeMatch;
                const routeOptions = activeRoute.def?.options ?? {};
                await this.activeModule.refresh(routeOptions, activeRoute);
            }

            this.invalidate();

        } finally {

            this.clearBusyFlag();

        }

    }

    async _createModuleFromRouteDef(routeDef, routeMatch) {

        const routeFactory = routeDef?.factory ?? null;
        const routeOptions = routeDef?.options ?? {};

        if (typeof routeFactory !== 'function')
            throw new Error(`[Application] Route "${routeDef?.name ?? '?'}" is missing factory callback.`);

        return await routeFactory(this, routeDef, routeMatch, routeOptions);

    }

    // --------------------------------------------------------------------------------------------

    showToast(type, message, description) {

        switch (type) {
            case 'error':
                this.toaster.error(message, description);
                break;
            case 'warning':
                toaster.warning(message, description);
                break;
            case 'success':
                this.toaster.success(message, description);
                break;
            default:
                this.toaster.info(message, description);
        }

    };

    handleError(error) {

        if (!error) {
            this.logger.error(`Uknown error occured.`);
            return;
        }

        if (error.details) {

            const innerError = error.details;

            const errorText = `[${innerError.code ?? '?'}] ${innerError.description ?? innerError.message}`;
            let errorData = null;

            if (innerError.details) {
                if (innerError.details.errors) {
                    errorData = Object.values(innerError.details.errors);
                } else if (innerError.details.fields) {
                    errorData = innerError.details.fields;
                }
            }

            this.logger.error(errorText, errorData);
            return;
        }

        if (error.error && error.error.code && error.error.message) {
            this.logger.error(`[${error.error.code}] ${error.error.message}`);
            return;
        }
        if (error.code && error.message) {
            this.logger.error(`[${error.code}] ${error.message}`);
            return;
        }
        if (error.message) {
            this.logger.error(error.message);
            return;
        }
        this.logger.error(error);

    };

    processApiError(error) {

        this.handleError(error);
        if (error.code == 401) {
            authManager.clearLoggedUser();
            //this.routeTo('auth-login');
            return;
        }

    };

    // --------------------------------------------------------------------------------------------

    _render(vm, app) {

        const ct = Services.get('templates');
        const el = defineElement;

        let activeModule = this.getAcitveModule(),
            appLogo = app.getAppLogo() || '/img/app-logo.png',
            appName = app.getAppName() || 'Application',
            loggedUser = this.authManager.getUser();

        return ct.vstrip('.app-container',
            [
                ct.cell('.app-navbar', [
                    ct.navbar([
                        ct.navbarSection([
                            ct.navbarBrand('.p-2', { href: '#' }, [
                                ct.img({ src: appLogo }),
                            ]),
                            ' ',
                            ct.navbarBrand('.p-2', { href: '#' }, [
                                appName
                            ]),
                        ]),
                        ct.navbarSection(
                            renderLangControls().concat(renderUserControls())
                        ),
                    ]),
                ]),
                ct.cell('.grid-x.app-content.off-canvas.off-canvas-sidebar-show', [
                    ct.cell('.app-sidebar.off-canvas-sidebar', [
                        '[Side Bar]'
                    ]),
                    ct.cell('.grid-y.app-module.off-canvas-content', [
                        ct.cell([
                            activeModule ? activeModule.render(vm) : 'No active module'
                        ])
                    ]),
                    el('a.off-canvas-overlay', { href: '#close' }),
                ]),
            ].concat(app.controls.map((control) => { return control.render(vm); }))
        );

        function renderLangControls() {

            return [
                ct.icon('language', '.mr-2'),
                ' ',
                ct.comboBox(app.getLocale(),
                    app.getAvailableLocales().map((def) => {
                        return {
                            text: def.name,
                            value: def.code,
                            data: def
                        };
                    }),
                    {
                        style: 'max-width:10rem;',
                        onchange: (e) => {
                            let locale = dom.getComboboxValue(e);
                            app.setLocale(locale);
                        }
                    }
                )
            ];

        };

        function renderUserControls() {

            const loginUrl = app.getRouteUrl('auth-login');

            if (!loggedUser) {

                return [
                    ct.link(loginUrl, [ct.icon('login'), ' ', i18n.t('app.label_login')], { appearance: 'button' }),
                ];

            }

            const accountUrl = app.getRouteUrl('account-details'),
                logoutUrl = app.getRouteUrl('auth-logout'),
                userName = (loggedUser.firstName || '') + ' ' + (loggedUser.lastName || '').toUpperCase(),
                userUsername = loggedUser.username,
                userDisplayName = userName != ' ' ? userName : userUsername;

            return [
                ct.link(accountUrl, [ct.icon('user'), ' ', userDisplayName], { appearance: 'button' }),
                ' ',
                ct.link(logoutUrl, [ct.icon('logout'), ' ', i18n.t('app.label_logout')], { appearance: 'button' }),
            ];

        };

    };

    // --------------------------------------------------------------------------------------------

    async start(rootElement, options = {}) {

        if (!rootElement)
            throw new Error('[Application] "rootElement" is required.');

        await this.prepare();

        const {
            consumeRootElement = true
        } = options;

        this._rootElement = rootElement;
        this._view.mount(rootElement, consumeRootElement);

        this.router.start();

        this.invalidate();

    };


}    
